import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';

import { spacing, statusPalette } from '@/theme/tokens';

export type StatusKind = 'healthy' | 'degraded' | 'unhealthy' | 'unknown';

const KIND_LABEL: Record<StatusKind, string> = {
  healthy: 'Healthy',
  degraded: 'Degraded',
  unhealthy: 'Unhealthy',
  unknown: 'Unknown',
};

const useStyles = makeStyles({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: spacing.xs,
    paddingLeft: spacing.s,
    paddingRight: spacing.s,
    paddingTop: spacing.xxs,
    paddingBottom: spacing.xxs,
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '12px',
    fontWeight: 600,
    lineHeight: '16px',
    // Defense-in-depth for flex parents (the dashboard card header is a
    // flex row with the name on the left + this pill on the right). A
    // long unbreakable resource name on a flex item with the default
    // `min-width: auto` will steal the pill's space and clip it (`De…`).
    // `flexShrink: 0` keeps the pill at its full width regardless of the
    // sibling's content, and `whiteSpace: nowrap` keeps the label from
    // wrapping inside its pill. Pushing this into the component means
    // every future call site is safe without remembering to wrap it.
    flexShrink: 0,
    whiteSpace: 'nowrap',
  },
  dot: {
    display: 'inline-block',
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
});

interface StatusPillProps {
  kind: StatusKind;
  label?: string;
  className?: string;
}

export function StatusPill({ kind, label, className }: StatusPillProps) {
  const classes = useStyles();
  const fg = statusPalette[kind];
  const bg = statusPalette[`${kind}Bg` as keyof typeof statusPalette];
  return (
    <span
      className={mergeClasses(classes.root, className)}
      style={{ color: fg, backgroundColor: bg }}
      role="status"
    >
      <span className={classes.dot} style={{ backgroundColor: fg }} aria-hidden />
      {label ?? KIND_LABEL[kind]}
    </span>
  );
}
